import { useEffect, useState } from 'react';
import {
  fetchZones,
  fetchMetrics,
  fetchLatest,
  fetchSyncRuns,
  fetchAlerts,
} from './api';
import type {
  Zone,
  MetricRow,
  LatestSummary,
  SyncRun,
  AlertRow,
} from './types';
import { MetricCard } from './components/MetricCard';
import { TrafficChart } from './components/TrafficChart';
import { EmptyState } from './components/EmptyState';
import { AlertList } from './components/AlertList';
import { SyncRunList } from './components/SyncRunList';

const WINDOWS = [
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
] as const;

type Window = typeof WINDOWS[number]['minutes'];

export function App() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [activeZone, setActiveZone] = useState<Zone | null>(null);
  const [latest, setLatest] = useState<LatestSummary | null>(null);
  const [rows, setRows] = useState<MetricRow[]>([]);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [syncRuns, setSyncRuns] = useState<SyncRun[]>([]);
  const [windowMin, setWindowMin] = useState<Window>(60);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const z = await fetchZones();
        setZones(z);
        setActiveZone(z[0] ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!activeZone) return;
    let cancelled = false;
    async function load() {
      try {
        const [latestData, metricsData, alertsData, syncData] = await Promise.all([
          fetchLatest(activeZone!.zone_id),
          fetchMetrics(activeZone!.zone_id, windowMin),
          fetchAlerts(activeZone!.zone_id, 50),
          fetchSyncRuns(10),
        ]);
        if (cancelled) return;
        setLatest(latestData);
        setRows(metricsData);
        setAlerts(alertsData);
        setSyncRuns(syncData);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    }
    load();
    const id = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeZone, windowMin]);

  if (error) {
    return (
      <main>
        <header>
          <h1>CF Monitor</h1>
        </header>
        <section className="error">
          <h2>⚠ 載入失敗</h2>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  if (loading) {
    return (
      <main>
        <header>
          <h1>CF Monitor</h1>
        </header>
        <p>載入中...</p>
      </main>
    );
  }

  if (!activeZone) {
    return (
      <main>
        <header>
          <h1>CF Monitor</h1>
        </header>
        <p>沒有已啟用的 zones。</p>
      </main>
    );
  }

  const hasData = rows.length > 0;
  const lastSync = syncRuns[0] ?? null;

  return (
    <main>
      <header>
        <h1>CF Monitor</h1>
        <p>
          監控中:<strong>{activeZone.domain}</strong>{' '}
          <span className="hint">
            ({zones.length} zone{zones.length !== 1 ? 's' : ''} registered,
            data_source: {activeZone.data_source})
          </span>
        </p>
      </header>

      {!hasData ? (
        <EmptyState
          lastSyncAt={lastSync?.completed_at ?? lastSync?.started_at ?? null}
        />
      ) : (
        <>
          {latest && (
            <MetricCard
              totals={latest.totals}
              ratios={latest.ratios}
              deltaPct={latest.vs_prev_window.total_requests_delta_pct}
            />
          )}

          <div className="window-switcher">
            {WINDOWS.map((w) => (
              <button
                key={w.minutes}
                className={w.minutes === windowMin ? 'active' : ''}
                onClick={() => setWindowMin(w.minutes)}
              >
                {w.label}
              </button>
            ))}
          </div>

          <TrafficChart rows={rows} />
        </>
      )}

      <AlertList alerts={alerts} />
      <SyncRunList runs={syncRuns} />

      <footer className="footer">
        <small>
          CF Monitor MVP · {zones.length} zone(s) · last sync:{' '}
          {lastSync?.status ?? 'none'}
          {lastSync?.completed_at
            ? ` at ${new Date(lastSync.completed_at * 1000).toISOString()}`
            : ''}
        </small>
      </footer>
    </main>
  );
}
